import React, { useState } from 'react';
import { 
  Users, 
  Bot, 
  Filter, 
  Database, 
  Send, 
  Calendar, 
  CheckCircle2, 
  ArrowRight,
  Zap
} from 'lucide-react';

export const AutomationPipeline: React.FC = () => {
  const [activeStep, setActiveStep] = useState(1);

  const pipelineSteps = [
    {
      step: 1,
      title: 'Visitor',
      badge: 'Traffic Inflow',
      icon: Users,
      headline: 'Targeted Visitor Lands on AI-Ready Web Page',
      details: 'Prospect arrives via Google Search, ChatGPT, Perplexity, or paid campaign. The page loads in <0.8s with tailored value propositions and zero confusing clutter.',
      techStack: 'Edge CDN · Schema Entity Routing · Next-Gen Image Compression',
      simulation: {
        type: 'visitor',
        data: 'Visitor from Nairobi / London researching "B2B AI Growth & SEO Solutions" arrives at 9:42 PM.',
      },
    },
    {
      step: 2,
      title: 'AI Assistant',
      badge: 'Autonomous Engagement',
      icon: Bot,
      headline: '24/7 Conversational AI Concierge Engages in Seconds',
      details: 'Instead of an abandoned static contact form, the on-page AI Concierge proactively greets the visitor, understands their natural-language questions, and guides them.',
      techStack: 'Xweba Conversational LLM · Brand Knowledge Base · Vector Indexing',
      simulation: {
        type: 'chat',
        data: 'AI: "Welcome! Are you looking to upgrade your search visibility on Google and ChatGPT, or automate your lead capture pipeline?"',
      },
    },
    {
      step: 3,
      title: 'Lead Qualification',
      badge: 'Scoring & Filter',
      icon: Filter,
      headline: 'Autonomous Qualification of Scope, Budget & Urgency',
      details: 'The AI gathers project specifics, assesses timeline requirements, and calculates whether the lead is a fit for AI Starter, Growth, or Pro packages without human intervention.',
      techStack: 'Intent Recognition · Budget Classifier · Dynamic Qualification Rules',
      simulation: {
        type: 'qualification',
        data: 'Visitor specifies $2k budget & 3-week timeline → Lead scored as "Tier A / High Intent" and tagged for priority follow-up.',
      },
    },
    {
      step: 4,
      title: 'CRM Sync',
      badge: 'Instant Data Flow',
      icon: Database,
      headline: 'Two-Way Sync Directly into Your Core CRM',
      details: 'The lead profile, contact details, company URL, and entire conversational summary are pushed immediately to HubSpot, Salesforce, Notion, or Pipedrive.',
      techStack: 'REST Webhooks · HubSpot / Salesforce API · Zapier / Make / n8n',
      simulation: {
        type: 'crm',
        data: 'Contact created in CRM: Apex Partners · Stage: "Qualified Inbound Opportunity" · Owner: Auto-assigned.',
      },
    },
    {
      step: 5,
      title: 'Instant Follow-up',
      badge: '<60s Response',
      icon: Send,
      headline: 'Automated WhatsApp & Email Alert Dispatched in 45 Seconds',
      details: 'Speed-to-lead is everything. The system triggers an automated WhatsApp message to the prospect and dispatches an instant push notification to your sales team.',
      techStack: 'WhatsApp Business API · Twilio / SendGrid · Slack / Mobile Alerts',
      simulation: {
        type: 'whatsapp',
        data: 'WhatsApp sent: "Hi Sarah, thank you for reaching out to Xweba! Here is your custom roadmap overview and direct calendar link."',
      },
    },
    {
      step: 6,
      title: 'Appointment',
      badge: 'Frictionless Booking',
      icon: Calendar,
      headline: 'Client Self-Schedules on Your Live Calendar',
      details: 'The prospect selects a verified mutual time slot with zero back-and-forth emails. Calendar invites and reminders are dispatched automatically.',
      techStack: 'Cal.com / Calendly API · Google / Outlook Calendar · Zoom / Meet Integration',
      simulation: {
        type: 'calendar',
        data: 'Strategy call booked for Thursday 2:00 PM · Automated calendar invitation and SMS reminder confirmed.',
      },
    },
    {
      step: 7,
      title: 'Closed Sale',
      badge: 'Revenue Realized',
      icon: CheckCircle2,
      headline: 'A High-Value Client Acquired on Autopilot',
      details: 'Your sales team joins a call with an educated, pre-qualified buyer who has already reviewed pricing and scope. Conversion rates jump by 3x.',
      techStack: 'Closed Won Pipeline · Automated Onboarding · Growth Retainer Trigger',
      simulation: {
        type: 'sale',
        data: 'Proposal approved: AI Growth Package + Monthly Growth Retainer initiated.',
      },
    },
  ];

  return (
    <section id="pipeline" className="py-24 bg-white border-b border-slate-200">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-slate-500">
            <span className="text-[#F26522] font-black">AI BUSINESS AUTOMATION</span>
            <span aria-hidden="true" className="text-slate-300">·</span>
            <span>END-TO-END FLOW</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B2D52] tracking-tight">
            Visitor → AI assistant → Lead → CRM → Follow-up → Appointment → Sale
          </h2>

          <p className="text-slate-600 text-base sm:text-lg">
            This is where Xweba becomes exponentially more valuable than ordinary web design. We connect your website directly into your client acquisition operations.
          </p>
        </div>

        {/* Interactive Pipeline Bar */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-6 mb-8 overflow-x-auto">
          <div className="flex items-center min-w-[680px] justify-between relative">
            {/* Connecting hairline */}
            <div className="absolute top-1/2 left-8 right-8 -translate-y-1/2 h-0.5 bg-slate-200 z-0" />

            {pipelineSteps.map((s) => {
              const Icon = s.icon;
              const isActive = activeStep === s.step;
              const isPast = activeStep > s.step;

              return (
                <button
                  key={s.step}
                  type="button"
                  onClick={() => setActiveStep(s.step)}
                  className="relative z-10 flex flex-col items-center group cursor-pointer focus:outline-hidden p-2 min-w-[72px] min-h-[48px]"
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-sm transition-all duration-200 ${
                      isActive
                        ? 'bg-[#F26522] text-white shadow-md shadow-orange-500/30 scale-110 ring-4 ring-orange-100'
                        : isPast
                        ? 'bg-[#0B2D52] text-white'
                        : 'bg-white border-2 border-slate-300 text-slate-500 group-hover:border-slate-400'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className={`mt-2 text-xs font-bold transition-colors ${
                      isActive ? 'text-[#F26522]' : 'text-slate-700'
                    }`}
                  >
                    {s.title}
                  </span>
                  <span className="text-[10px] text-slate-400 font-mono">Step 0{s.step}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step Deep-Dive Card */}
        {(() => {
          const current = pipelineSteps.find((s) => s.step === activeStep) || pipelineSteps[0];
          const CurrentIcon = current.icon;

          return (
            <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-10 border border-slate-800 shadow-xl">
              <div className="grid lg:grid-cols-12 gap-8 items-center">
                
                {/* Left Description */}
                <div className="lg:col-span-7 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="bg-[#F26522] text-white text-[11px] font-mono font-bold px-2.5 py-0.5 rounded-md">
                      STAGE 0{current.step} OF 07
                    </span>
                    <span className="text-xs text-slate-400 font-medium">{current.badge}</span>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
                    {current.headline}
                  </h3>

                  <p className="text-slate-300 text-sm leading-relaxed">
                    {current.details}
                  </p>

                  <div className="pt-2 text-xs font-mono text-slate-400">
                    <span className="text-[#F26522] font-bold">Tech Stack:</span> {current.techStack}
                  </div>

                  {/* Next Step Control */}
                  <div className="pt-4 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setActiveStep((prev) => (prev % 7) + 1)}
                      className="px-4 py-2.5 min-h-[44px] bg-slate-800 hover:bg-slate-700 text-xs font-bold rounded-xl border border-slate-700 transition-colors inline-flex items-center gap-2 cursor-pointer"
                    >
                      <span>Simulate Next Step (0{((current.step % 7) + 1)})</span>
                      <ArrowRight className="w-3.5 h-3.5 text-[#F26522]" />
                    </button>
                  </div>
                </div>

                {/* Right Interactive Simulation Preview */}
                <div className="lg:col-span-5 bg-slate-950/80 rounded-2xl p-5 border border-slate-800/90 font-mono text-xs space-y-3">
                  <div className="flex items-center justify-between text-[11px] text-slate-400 border-b border-slate-800 pb-2">
                    <span className="flex items-center gap-1.5 text-emerald-400">
                      <Zap className="w-3.5 h-3.5" />
                      Live Automation Event Preview
                    </span>
                    <span className="text-[10px] text-slate-500">Real-time</span>
                  </div>

                  <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 text-slate-200 text-xs leading-relaxed">
                    {current.simulation.data}
                  </div>

                  <div className="space-y-1 text-[11px] text-slate-400 pt-1">
                    <div className="flex justify-between">
                      <span>Latency:</span>
                      <span className="text-emerald-400">&lt; 45 seconds</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Human Labor Required:</span>
                      <span className="text-slate-300">0 minutes (100% Autonomous)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Lead Retention:</span>
                      <span className="text-[#F26522] font-bold">99.4% Captured</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          );
        })()}

      </div>
    </section>
  );
};
